import { defineStore } from 'pinia';
import type { ComputedRef } from 'vue';

import { useApiChoicesStore } from '@/features//ChoicesLists/Api/store';
import { API_CHOICE } from '@/features//ChoicesLists/Api/types';
import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
} from '@/features/ChoicesLists/Simple/types';
import {
  type ClientResourceContainer,
  type ClientResourcePersisted,
  ROLE,
  useClient,
} from '@/features/Clients';
import { useInputStore } from '@/features/Sparten';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import { generateURL } from '@/utils/generateURLs/generateURLs';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';
import { HttpServiceProvider } from '@/services/api/HttpServiceProvider';

const getPersistedVN = (): ComputedRef<
  ClientResourceContainer<ClientResourcePersisted> | undefined
> => useClient(ROLE.VERSICHERUNGSNEHMER).persistedClient;

export const PRELIMINARY_CONTRACTS_CHOICE_ID = 'preliminary.contracts';

export interface State {
  data: {
    isPossible: boolean;
    hasSignature: boolean;
    hasBrokerContract: boolean;
  };

  preliminaryContractLink: string | null;
}

export const usePreviousInsuranceStore = defineStore('PreviousInsuranceStore', {
  state: (): State => ({
    data: {
      hasBrokerContract: false,
      isPossible: false,
      hasSignature: false,
    },

    preliminaryContractLink: null,
  }),

  actions: {
    async checkCanCancel() {
      if (!this.canCancel) {
        return;
      }

      const vn = getPersistedVN().value;

      try {
        // can't be null as canCancel would return false then.
        if (vn === undefined) {
          throw new Error('VN not persisted');
        }

        const api = useApi().previousInsurance;

        const [, responsePromise] = api.canCancel(vn.data.id.toString());
        const result = await responsePromise;

        if (result === null) {
          throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
            additionalErrorMessage: 'previousInsurance -> canCancel',
          });
        }

        this.data.hasBrokerContract = result.maklerauftrag;
        this.data.isPossible = result.moeglich;
        this.data.hasSignature = result.unterschrift;
      } catch (error) {
        throw new VglError(
          VGL_ERROR_CODE.PREVIOUS_INSURANCE_CAN_CANCEL_CHECK_FAILED,
          {
            additionalErrorMessage: `error: ${getErrorMessage(
              error,
            )} | clientId: ${vn?.data.id ?? 'not persisted'}`,
            cause: error,
          },
        );
      }
    },

    async requestPreliminaryContracts() {
      const vn = getPersistedVN().value;

      if (vn !== undefined) {
        return useApiChoicesStore().loadChoice({
          type: API_CHOICE.PRELIMINARY_CONTRACTS,
          requestParameters: [vn.data.id.toString()],
          id: PRELIMINARY_CONTRACTS_CHOICE_ID,
        });
      }

      return Promise.resolve();
    },

    async loadPreliminaryContract(customLink?: string) {
      try {
        const api = useApi().previousInsurance;

        const [, responsePromise] = api.loadPreliminaryContract(
          customLink ?? this.preliminaryContractLink ?? '',
        );
        return await responsePromise;
      } catch (error) {
        throw new VglError(
          VGL_ERROR_CODE.PREVIOUS_INSURANCE_LOADING_PRELIMINARY_CONTRACT_FAILED,
          {
            additionalErrorMessage: `error: ${getErrorMessage(
              error,
            )} | contract url: ${this.preliminaryContractLink}`,
            cause: error,
          },
        );
      }
    },
  },

  getters: {
    hasPreviousInsurance() {
      const config = getCurrentSpartenConfig();
      if (config.hasPreviousInsurance !== undefined) {
        return config.hasPreviousInsurance();
      }

      return getSimpleChoiceMeta(
        SIMPLE_CHOICE.VORVERSICHERUNG,
        SIMPLE_CHOICE_META.VORSCHADEN_EXISTS,
      ).includes(useInputStore().vorversicherung as string);
    },

    hasAutomaticTermination() {
      const config = getCurrentSpartenConfig();

      return config.hasAutomaticTermination !== undefined
        ? config.hasAutomaticTermination()
        : this.hasPreviousInsurance;
    },

    canCancel() {
      const vn = getPersistedVN().value;
      return vn !== undefined && vn.data.id > 0;
    },

    brokerSignatureMissingUrl() {
      return generateURL(
        HttpServiceProvider.getPwUrl(),
        '/profile/update?active_tab=unterschrift',
      );
    },

    brokerContractMissingUrl() {
      const vn = getPersistedVN().value;

      if (vn === undefined) {
        return '';
      }

      return generateURL(
        HttpServiceProvider.getPwUrl(),
        `/home/kundenakte/dokumente/anlegen?clientId=${vn.data.id}`,
      );
    },
  },
});
